import { CandidateStatus } from 'generated-graphql/types';
import { useGetCandidatesWithStatusByCandidateBatchQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useState, useEffect, useCallback } from 'react';
import { useOrganization } from 'core/stores/hooks/useOrganization';

export type CandidateWithStatus = {
  id: string;
  status: CandidateStatus;
};

export const useGetAllCandidatesByBatch = (
  candidateBatchId: string,
  limit = 500
) => {
  const organization = useOrganization();
  const [allCandidates, setAllCandidates] = useState<CandidateWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    loading,
    error: queryError,
    refetch
  } = useGetCandidatesWithStatusByCandidateBatchQuery({
    variables: {
      candidateBatchId,
      limit,
      organizationId: organization.id
    },
    skip: !candidateBatchId
  });

  useEffect(() => {
    if (queryError) {
      setError(queryError.message);
    }
  }, [queryError]);

  const fetchAllCandidates = useCallback(async () => {
    if (!candidateBatchId) return;

    setIsLoading(true);
    setError(null);

    try {
      let nextToken: string | null = null;
      const candidates: CandidateWithStatus[] = [];

      do {
        const result = await refetch({
          candidateBatchId,
          limit,
          nextToken
        });

        const response = result.data?.getCandidatesByCandidatesBatch;

        if (response?.items) {
          // Filter out null values and ensure proper typing
          const validItems = response.items
            .filter((item): item is NonNullable<typeof item> => item !== null)
            .map((item) => ({
              id: item.id,
              status: item.status as CandidateStatus
            }));
          candidates.push(...validItems);
        }

        nextToken = response?.nextToken || null;
      } while (nextToken);

      setAllCandidates(candidates);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch candidates';
      setError(errorMessage);
      console.error('Error fetching all candidates:', err);
    } finally {
      setIsLoading(false);
    }
  }, [candidateBatchId, limit, refetch]);

  return {
    candidates: allCandidates,
    loading: loading || isLoading,
    error,
    refetch: fetchAllCandidates
  };
};
